import { FormattedMessage } from 'react-intl';

import { Gender } from '@/shared/api/protocol-ts/model/dto_base_pb';
import {
  MAX_NAME_LENGTH,
  MAX_EMAIL_LENGTH,
  MAX_EXTERNAL_ID_LENGTH,
} from '@/shared/config';
import {
  array,
  date,
  InferType,
  number,
  object,
  string,
} from '@/shared/config/yup';
import { getMaxDateOfBirthForMinAge } from '@/shared/lib/ISO';

import { addDoctorsSchema } from '@/features/treatingDoctors';

const defaultPatientSchema = object({
  firstName: string().required().max(MAX_NAME_LENGTH),
  lastName: string().required().max(MAX_NAME_LENGTH),
  email: string().email().max(MAX_EMAIL_LENGTH),
  patientExternalID: string().max(MAX_EXTERNAL_ID_LENGTH),
  dateOfBirth: date().required(),
  gender: number()
    .required()
    .oneOf([Gender.GenderMale, Gender.GenderFemale, Gender.GenderOther]),
});

export const newPatientSchema = defaultPatientSchema.concat(addDoctorsSchema);

export const editPatientSchema = defaultPatientSchema.concat(
  object({
    treatingDoctors: array().notRequired(),
  }),
);

export const patientSchema = object({
  firstName: string().required().max(MAX_NAME_LENGTH),
  lastName: string().required().max(MAX_NAME_LENGTH),
  email: string().email().max(MAX_EMAIL_LENGTH),
  patientExternalID: string().max(MAX_EXTERNAL_ID_LENGTH),
  dateOfBirth: date().required(),
  gender: number()
    .required()
    .oneOf([Gender.GenderMale, Gender.GenderFemale, Gender.GenderOther]),
}).concat(addDoctorsSchema);

type GetPatientValidationSchemaOptions = {
  validateDoctors: boolean;
  minimumAge?: number;
};

export const getPatientValidationSchema = ({
  validateDoctors,
  minimumAge,
}: GetPatientValidationSchemaOptions) => {
  const maxDateOfBirth =
    minimumAge !== undefined
      ? getMaxDateOfBirthForMinAge(minimumAge)
      : new Date();

  return object({
    firstName: string().required().max(MAX_NAME_LENGTH),
    lastName: string().required().max(MAX_NAME_LENGTH),
    email: string().email().max(MAX_EMAIL_LENGTH),
    patientExternalID: string().max(MAX_EXTERNAL_ID_LENGTH),
    dateOfBirth: date()
      .nullable()
      .required()
      .max(maxDateOfBirth, () => (
        <FormattedMessage
          id="patientModalForm.validation.minimumAge"
          defaultMessage="Patient must be at least {minAge} years old"
          values={{ minAge: minimumAge }}
        />
      )),
    gender: number()
      .required()
      .oneOf([Gender.GenderMale, Gender.GenderFemale, Gender.GenderOther]),
    treatingDoctors: validateDoctors
      ? array()
          .of(string())
          .min(1, () => (
            <FormattedMessage
              id="addDoctors.validationError"
              defaultMessage="You should select at least one doctor"
            />
          ))
          .required()
      : array().notRequired(),
  });
};

export type PatientPayload = InferType<typeof patientSchema>;

export type NewPatientPayload = InferType<typeof newPatientSchema>;
export type EditPatientPayload = InferType<typeof editPatientSchema>;

export const DATE_OF_BIRTH_FORMAT = 'yyyy-MM-dd';

export const PATIENT_FORM_ID = 'patient-form';
